import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { PaginationMetadata } from '../../../models/paged-response.model';
import { FormsModule } from '@angular/forms';


@Component({
  selector: 'app-pagination-controls',
  standalone: true,
  imports: [CommonModule, TranslatePipe, FormsModule],
  templateUrl: './pagination-controls.component.html',
  styleUrl: './pagination-controls.component.scss'
})
export class PaginationControlsComponent {

  /**
   * Receives pagination metadata from the parent component.
   */
  @Input({ required: true }) pagination!: PaginationMetadata;

  /**
   * Array of page size options to display in the dropdown.
   */
  @Input() pageSizeOptions: number[] = [10, 25, 50, 100];

  /**
   * Emits the new page number when the user clicks a navigation button.
   */
  @Output() pageChange = new EventEmitter<number>();

  /**
   * Emits the new page size when the user changes it.
   */
  @Output() pageSizeChange = new EventEmitter<number>();

  /**
   * Generates an array of page numbers and ellipsis markers ('...') to display in the UI.
   * Logic ensures the first page, the last page, and a small range around the current 
   * page (2 pages before/after) are displayed.
   * @returns An array of page numbers (number) and ellipsis markers (string).
   */
  getPages(): (number | string)[] {
    const totalPages = this.pagination.total_pages;
    const currentPage = this.pagination.page;
    const pageRange = 2; // Show 2 pages before and 2 pages after the current page

    if (totalPages <= 1) {
        return [];
    }
    
    // Use a Set to easily handle unique page numbers
    const pageSet = new Set<number>();

    // 1. Always include the first and last page
    pageSet.add(1);
    pageSet.add(totalPages);

    // 2. Include pages around the current page
    for (let i = currentPage - pageRange; i <= currentPage + pageRange; i++) {
        if (i >= 1 && i <= totalPages) {
            pageSet.add(i);
        }
    }
    
    // 3. Convert the Set to a sorted Array
    const uniquePages = Array.from(pageSet).sort((a, b) => a - b);
    
    // 4. Insert ellipsis markers
    const finalPages: (number | string)[] = [];
    let lastPage = 0;
    
    for (const page of uniquePages) {
        // If the gap between the current page and the last displayed page is greater than 1, insert '...'
        if (page > lastPage + 1) {
            finalPages.push('...');
        }
        finalPages.push(page);
        lastPage = page;
    }

    return finalPages;
  }

  /**
   * Handles click events on page buttons and emits the page change event.
   * Changed signature to accept string | number to satisfy the Angular template parser.
   * @param newPage The page number (or '...' string) to navigate to.
   */
  onPageChange(newPage: number | string): void {
    // Only proceed if the input is definitely a number
    if (typeof newPage === 'number') {
      if (newPage > 0 && newPage <= this.pagination.total_pages) {
        this.pageChange.emit(newPage);
      }
    }
  }

  onPageSizeChange(newSize: number): void {
    // L'objet newSize est déjà le nombre d'éléments par page.
    if (newSize > 0) {
      // Émet la nouvelle taille pour que le composant parent puisse recharger la première page
      this.pageSizeChange.emit(newSize); 
    }
  }
}
